import type { Options, Value } from './types';

export const getFormatter = ({
  currency,
  display = 'narrowSymbol',
  locale,
  noFraction,
  ...rest
}: Options & { noFraction?: boolean }): Intl.NumberFormat => {
  let minimumFractionDigits: number | undefined;
  let maximumFractionDigits: number | undefined;

  if (rest.notation === 'compact') {
    // Compact notation fraction digits are unrelated to minor units, they are just used to show greater precision
    // when needed. Being able to always show 2 decimal places better aligns with our currency writing guide.
    minimumFractionDigits = rest.showFraction ? 2 : undefined;
    maximumFractionDigits = rest.showFraction ? 2 : undefined;
  }

  if (noFraction) {
    minimumFractionDigits = 0;
  }

  const numberFormatOptions = {
    currency,
    currencyDisplay: display === 'noSymbol' ? undefined : display,
    notation: rest.notation ?? 'standard',
    style: display === 'noSymbol' ? 'decimal' : 'currency',
    minimumFractionDigits,
    maximumFractionDigits,
  } as const;

  try {
    return new Intl.NumberFormat(locale, numberFormatOptions);
  } catch {
    // Fall back to `currencyDisplay: 'symbol'` if `narrowSymbol` was tried
    // `symbol` has much better support than `narrowSymbol`
    //
    // Also fall back to `notation: 'standard'` by deleting `notation` from the options object
    // as `currencyDisplay: 'narrowSymbol'` and `notation` support was added to safari
    // together.
    //
    // Safari lagged behind in support for some `Intl.NumberFormat` constructor options,
    // and we still support Safari 9.1, so these fallbacks are required for the time being.
    //
    // This also handles the case where the user's linked ICU library from their OS does not support
    // the options supported by the browser. In newer versions of safari, probably 15.x, this is no
    // longer an issue.
    // See https://bugs.webkit.org/show_bug.cgi?id=226861.
    //
    // See https://caniuse.com/mdn-javascript_builtins_intl_numberformat_numberformat_options_currencydisplay_parameter
    // and https://caniuse.com/mdn-javascript_builtins_intl_numberformat_numberformat_options_notation_parameter.

    // @ts-expect-error `notation` is optional in the `Intl.NumberFormat` constructor
    delete numberFormatOptions.notation;

    return new Intl.NumberFormat(locale, {
      ...numberFormatOptions,
      currencyDisplay:
        numberFormatOptions.currencyDisplay === 'narrowSymbol'
          ? 'symbol'
          : numberFormatOptions.currencyDisplay,
    });
  }
};

export const getFormatterByValue = (
  value: Value,
  options: Options,
): Intl.NumberFormat => {
  const noFraction =
    options.dropFraction && (typeof value === 'bigint' || value % 1 === 0);

  return getFormatter({ ...options, noFraction });
};
